// The return-to-app flow: query-parameter parsing, the app-origin allow-list, and
// complete() — what happens once an SSO session exists.
//
// Two ways an app sends the user here (see payreq-app/src/lib/sso-client.ts, the
// org-standard client snippet):
//
//   Popup   /?mode=popup&origin=<opener origin>
//           On success we postMessage {type:"rgseva-sso:complete"} to the opener at
//           exactly that origin (only if allow-listed) and close the window. The opener
//           then calls POST /sso/token itself — NO tokens ever travel via postMessage.
//
//   Redirect /?next=<absolute URL back into the app>
//           On success we location.replace(next), but only when next starts with an
//           allow-listed app origin — an open redirect otherwise.
//
// The allow-list comes from VITE_ALLOWED_APP_ORIGINS (comma-separated origins).

const DEV_DEFAULT_ORIGINS = "http://localhost:5175,http://localhost:5173";

export const ALLOWED_APP_ORIGINS: string[] = (
  (import.meta.env.VITE_ALLOWED_APP_ORIGINS as string | undefined) ?? DEV_DEFAULT_ORIGINS
)
  .split(",")
  .map((s) => s.trim().replace(/\/+$/, ""))
  .filter(Boolean);

export interface FlowParams {
  /** Validated absolute URL to return to, or null. */
  next: string | null;
  /** Popup mode requested AND the opener origin is allow-listed. */
  popup: boolean;
  /** The validated opener origin (popup mode only). */
  openerOrigin: string | null;
}

/** `url` starts with an allow-listed origin at a real boundary ("", "/", "?", "#"). */
function startsWithAllowedOrigin(url: string): boolean {
  return ALLOWED_APP_ORIGINS.some((origin) => {
    if (!url.startsWith(origin)) return false;
    const boundary = url.charAt(origin.length);
    return boundary === "" || boundary === "/" || boundary === "?" || boundary === "#";
  });
}

export function readFlowParams(search: string = window.location.search): FlowParams {
  const params = new URLSearchParams(search);

  const rawNext = params.get("next");
  const next = rawNext && startsWithAllowedOrigin(rawNext) ? rawNext : null;

  const rawOrigin = (params.get("origin") ?? "").replace(/\/+$/, "");
  const originAllowed = rawOrigin !== "" && ALLOWED_APP_ORIGINS.includes(rawOrigin);

  const popupRequested = params.get("mode") === "popup";

  return {
    next,
    // Popup messaging is REFUSED unless the declared opener origin is allow-listed.
    popup: popupRequested && originAllowed,
    openerOrigin: popupRequested && originAllowed ? rawOrigin : null,
  };
}

/** Message the opener listens for. Deliberately carries NO token — just a signal. */
export const COMPLETE_MESSAGE_TYPE = "rgseva-sso:complete";

export type CompleteOutcome =
  | "popup-signalled" // opener notified + window.close() attempted; show the fallback note
  | "redirected" // navigating to `next`; nothing more to render
  | "stay"; // nowhere to go — show the "You're signed in" card

/**
 * The SSO session exists — hand control back to the app that sent us here.
 * Never sends tokens anywhere; the app fetches its own via POST /sso/token.
 */
export function completeSignIn(flow: FlowParams): CompleteOutcome {
  if (flow.popup && flow.openerOrigin && window.opener) {
    try {
      (window.opener as Window).postMessage({ type: COMPLETE_MESSAGE_TYPE }, flow.openerOrigin);
    } catch {
      // opener gone — the app's popup-closed poll still picks the session up
    }
    window.close(); // may be ignored (window not script-opened) → caller shows fallback
    return "popup-signalled";
  }
  if (flow.next) {
    window.location.replace(flow.next);
    return "redirected";
  }
  return "stay";
}
